/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import axios, { isAxiosError } from 'axios';

import { BAError } from '../../types/requests';
import { getAuthToken, handleAuthError } from '../../utils/auth';
import { BAProfile, BAProvidersSync } from './user.models';
import { datadogRum } from '@datadog/browser-rum';

const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BA_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getAuthHeader = () => {
  const authToken = getAuthToken();
  if (authToken) {
    return { Authorization: `Bearer ${authToken.token}` };
  }
};

const getIdFromAuthToken = (): string => {
  const authToken = getAuthToken();
  if (!authToken || !authToken.token) {
    throw new Error('Auth token not found');
  }
  
  const payload = authToken.token.split('.')[1];
  if (!payload) {
    throw new Error('Invalid token format');
  }
  const decodedPayload = atob(payload);
  const parsedPayload = JSON.parse(decodedPayload) as { sub: string };
  
  return parsedPayload.sub;
};

export const getUserProfile = async (): Promise<BAProfile> => {
  try {
    const profileResponse = await api.get<BAProfile>('/players/me', {
      headers: getAuthHeader(),
    });
    const profile = profileResponse.data;
    const id = getIdFromAuthToken();

    datadogRum.setUser({
      email: profile.email,
      name: profile.nickName,
      id,
    });

    return profile;
  } catch (error) {
    if (isAxiosError<BAError>(error) && error.response) {
      handleAuthError(error.response.data);
    }
    throw error;
  }
};

export const getUserProviders = async (): Promise<BAProvidersSync[]> => {
  try {
    const providersResponse = await api.get<BAProvidersSync[]>('/players/me/game-accounts/dbd', {
      headers: getAuthHeader(),
    });
    const providers = providersResponse.data;

    return providers;
  } catch (error) {
    if (isAxiosError<BAError>(error) && error.response) {
      handleAuthError(error.response.data);
    }
    throw error;
  }
};
